/**
 * Role-split sign-in (2026-07-29): remembers which door the user last signed in through.
 *
 * WHY THIS EXISTS: the sign-in page opens by asking "parent or student?". Without a memory
 * every returning user pays an extra tap, forever, to answer a question they already
 * answered — which is the opposite of what the split was for.
 *
 * LIFECYCLE, and the distinction is deliberate:
 *   - kept across session EXPIRY — that is the same person coming back, and re-asking
 *     tells them nothing we don't already know.
 *   - cleared on EXPLICIT logout (useAuth.logout) — on a shared family device, deliberately
 *     signing out is precisely the moment somebody else wants the other door.
 *
 * This is a display preference and nothing more. It does not decide where anyone lands
 * after signing in — that comes from the account's Cognito groups (getPostLoginRoute) — and
 * it is not an authorization signal. A tampered value costs the user one wrong-looking form.
 */

export type SignInRole = 'parent' | 'student';

const STORAGE_KEY = 'thinkotter.signin.role';

/** Narrow an unknown stored string, so a stale or hand-edited value can't select a form. */
const isRole = (value: unknown): value is SignInRole => value === 'parent' || value === 'student';

/**
 * Every access is wrapped: localStorage throws in Safari private mode and can be disabled
 * outright by browser settings. A remembered role is a convenience, so failing to read or
 * write one must degrade to "show the picker", never to a broken sign-in page.
 */
export function readRememberedRole(): SignInRole | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isRole(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function rememberRole(role: SignInRole): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, role);
  } catch {
    // Non-fatal: the user simply gets the picker again next time.
  }
}

export function forgetRememberedRole(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal.
  }
}
